import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import {
  useGetRostersForGroup,
  useSendSeatActivationRequests,
} from 'hooks/incident/useRoster';
import { selectUserGuid } from 'slices/commonSelectors';
import { useHwcgFlag } from 'utils/featureFlags';

import { useHwcgActivationBlockedReason } from './hwcgMemberCompanyExclusive';
import { seatRequestState, type SeatRequestState } from './rosterPeriods';

function shortTime(time: Date | null): string {
  if (!time) return '';
  return time.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function statusChip(state: SeatRequestState, requestedByName: string | null) {
  switch (state.kind) {
    case 'not_sent':
      return { variant: 'secondary' as const, label: 'Not sent', title: '' };
    case 'requested':
      return {
        variant: 'amber' as const,
        label: 'Requested',
        title: [
          state.requestedAt ? `Sent ${shortTime(state.requestedAt)}` : '',
          requestedByName ? `by ${requestedByName}` : '',
        ]
          .filter(Boolean)
          .join(' '),
      };
    case 'no_reply':
      return {
        variant: 'orange' as const,
        label: `No reply after ${state.after}`,
        title: state.requestedAt
          ? `Last sent ${shortTime(state.requestedAt)}`
          : '',
      };
    case 'accepted':
      return {
        variant: 'green' as const,
        label: 'Accepted',
        title: state.respondedAt ? shortTime(state.respondedAt) : '',
      };
    case 'declined':
      return {
        variant: 'destructive' as const,
        label: 'Declined',
        title: state.note ?? '',
      };
  }
}

function actionLabel(state: SeatRequestState): string | null {
  switch (state.kind) {
    case 'not_sent':
      return 'Send request';
    case 'requested':
      return 'Resend';
    case 'no_reply':
      return 'Resend now';
    case 'declined':
      return 'Send again';
    case 'accepted':
      return null;
  }
}

/**
 * HWCG: one seat's activation request status and the single action that fits
 * it. Hidden with the flag off and on the viewer's own seat.
 */
export function SeatActivationRequestControl({
  incidentId,
  groupId,
  positionId,
  member,
  plannedPeriodId,
  caption,
  className,
}: {
  incidentId: string;
  groupId: string;
  positionId: string;
  member: Pick<RosterEntry, 'userId' | 'incidentPositions'>;
  /** The planned period's roster; omitted for the current period. */
  plannedPeriodId?: string | null;
  caption?: string;
  className?: string;
}) {
  const hwcg = useHwcgFlag();
  const viewerGuid = useSelector(selectUserGuid);
  const { data: groupRoster = [] } = useGetRostersForGroup(groupId);
  const send = useSendSeatActivationRequests(incidentId, groupId);
  const blockedReason = useHwcgActivationBlockedReason(
    incidentId,
    groupId,
    member.userId
  );
  const [now, setNow] = useState(() => Date.now());

  const position = member.incidentPositions?.find(
    (item) => item.positionId === positionId
  );
  const state = seatRequestState(position ?? {}, now);
  const cooldownEndsAt =
    state.kind === 'requested' ? state.cooldownEndsAt : null;
  const unlockAt = cooldownEndsAt?.getTime() ?? null;

  useEffect(() => {
    if (unlockAt == null) return;
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.max(0, unlockAt - Date.now()) + 250
    );
    return () => window.clearTimeout(timer);
  }, [unlockAt]);

  if (!hwcg || !position || member.userId === viewerGuid) return null;

  const requestedBy = position.activationRequestedBy;
  const requestedByName = requestedBy
    ? (groupRoster.find((person) => person.user_guid === requestedBy)
        ?.user_name ?? null)
    : null;
  const chip = statusChip(state, requestedByName);
  const label = actionLabel(state);
  const coolingDown = unlockAt != null;

  return (
    <div className={cn('tw:flex tw:items-center tw:gap-2', className)}>
      {caption ? (
        <span className="tw:text-xs tw:text-muted-foreground">{caption}</span>
      ) : null}
      <Badge variant={chip.variant} title={chip.title || undefined}>
        {chip.label}
      </Badge>
      {label ? (
        <Button
          type="button"
          size="sm"
          variant={state.kind === 'not_sent' ? 'default' : 'outline'}
          disabled={send.isPending || coolingDown || Boolean(blockedReason)}
          title={
            blockedReason ??
            (coolingDown
              ? `Available at ${shortTime(cooldownEndsAt)}`
              : undefined)
          }
          onClick={() =>
            send.mutate({
              plannedPeriodId,
              seats: [{ userGuid: member.userId, positionId }],
            })
          }
        >
          {label}
        </Button>
      ) : null}
      {label && blockedReason ? (
        <span className="tw:text-xs tw:text-amber-300">{blockedReason}</span>
      ) : null}
    </div>
  );
}
